import { Download } from "lucide-react";

const LOGOS = [
  {
    id: "wordmark-bold",
    title: "01 · Bold Wordmark",
    desc: "Confident deep-navy sans-serif. Boardroom-ready. Works at any size.",
    src: "/01_wordmark_mono.png",
    bg: "#F5F2EC",
    file: "opsiq-01-wordmark-bold.png",
  },
  {
    id: "lockup",
    title: "02 · Lockup with Growth Mark",
    desc: "Abstract rising bar merges into a data-point node. Teal accent. Best as master brand.",
    src: "/02_lockup_with_mark.png",
    bg: "#F5F2EC",
    file: "opsiq-02-lockup.png",
  },
  {
    id: "monogram",
    title: "03 · Monogram Badge",
    desc: "Interlocked O/Q with transformation arrow. Ideal for favicon, app icon, social avatar.",
    src: "/03_monogram_badge.png",
    bg: "#0B2545",
    file: "opsiq-03-monogram-badge.png",
  },
  {
    id: "wordmark-light",
    title: "04 · Light Wordmark",
    desc: "Thinner geometric variant with a more editorial feel.",
    src: "/04_wordmark_q_dot.png",
    bg: "#F5F2EC",
    file: "opsiq-04-wordmark-light.png",
  },
];

const KIT = [
  {
    id: "kit-tagline",
    title: "Master Lockup with Tagline",
    desc: "Wordmark + rule + \"INTELLIGENCE, IN OPERATION.\" — use on covers, decks, homepage hero.",
    src: "/opsiq-brand-navy-tagline.png",
    bg: "#F5F2EC",
    file: "opsiq-master-lockup-tagline.png",
    span: true,
  },
  {
    id: "kit-navy-cream",
    title: "Wordmark · Navy on Cream",
    desc: "Primary application on light backgrounds.",
    src: "/opsiq-brand-navy-cream.png",
    bg: "#F5F2EC",
    file: "opsiq-wordmark-navy-cream.png",
  },
  {
    id: "kit-cream-navy",
    title: "Wordmark · Cream on Navy",
    desc: "Reversed application for dark decks, covers, event backdrops.",
    src: "/opsiq-brand-cream-navy.png",
    bg: "#0B2545",
    file: "opsiq-wordmark-cream-navy.png",
  },
  {
    id: "kit-black-white",
    title: "Wordmark · Mono Black",
    desc: "For print, invoices, contracts, single-colour applications.",
    src: "/opsiq-brand-black-white.png",
    bg: "#FFFFFF",
    file: "opsiq-wordmark-mono-black.png",
  },
  {
    id: "kit-white-black",
    title: "Wordmark · Mono Reversed",
    desc: "For dark UI, event signage, embossing.",
    src: "/opsiq-brand-white-black.png",
    bg: "#000000",
    file: "opsiq-wordmark-mono-white.png",
  },
  {
    id: "kit-monogram-navy",
    title: "Monogram · OQ Badge",
    desc: "Square app icon and favicon. Interlocked O/Q with transformation arrow.",
    src: "/opsiq-brand-monogram-navy.png",
    bg: "#FFFFFF",
    file: "opsiq-monogram-oq.png",
  },
];


export default function OpsIQLogos() {
  return (
    <div data-testid="opsiq-logos-page" className="min-h-screen bg-[#F4F4F0] text-[#0B2545]">
      <header className="max-w-[1400px] mx-auto px-5 md:px-10 pt-16 md:pt-24 pb-10">
        <div className="text-xs uppercase tracking-[0.25em] font-semibold text-[#00B4A6] mb-4">
          Logo Concepts · v1
        </div>
        <h1 className="font-display text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[0.95] max-w-3xl">
          OpsIQ — professional logo directions.
        </h1>
        <p className="mt-6 text-base md:text-lg text-[#4A4A4A] max-w-2xl">
          Four directions for a business transformation brand. Click a logo to download the
          PNG. Tell me which direction to refine and I&apos;ll iterate.
        </p>
      </header>

      <main className="max-w-[1400px] mx-auto px-5 md:px-10 pb-24">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          {LOGOS.map((l) => (
            <article
              key={l.id}
              data-testid={`logo-card-${l.id}`}
              className="group border border-[#0B2545]/15 bg-white overflow-hidden"
            >
              <div
                className="relative aspect-[4/3] flex items-center justify-center overflow-hidden"
                style={{ backgroundColor: l.bg }}
              >
                <img
                  src={l.src}
                  alt={l.title}
                  data-testid={`logo-img-${l.id}`}
                  className="max-w-[85%] max-h-[85%] object-contain"
                />
              </div>
              <div className="p-6 md:p-8 flex flex-col gap-4">
                <div>
                  <div className="text-[11px] uppercase tracking-[0.25em] font-semibold text-[#00B4A6] mb-2">
                    Concept
                  </div>
                  <h2 className="font-display text-2xl md:text-3xl leading-tight tracking-tight text-[#0B2545]">
                    {l.title}
                  </h2>
                  <p className="mt-3 text-sm md:text-base text-[#4A4A4A] leading-relaxed">
                    {l.desc}
                  </p>
                </div>
                <a
                  href={l.src}
                  download={l.file}
                  data-testid={`logo-download-${l.id}`}
                  className="inline-flex items-center gap-2 self-start px-5 py-3 bg-[#0B2545] text-white text-xs uppercase tracking-[0.2em] font-semibold hover:bg-[#00B4A6] hover:text-[#0B2545] transition-colors"
                >
                  <Download size={14} /> Download PNG
                </a>
              </div>
            </article>
          ))}
        </div>

        <div className="mt-16 p-8 md:p-10 border border-[#0B2545]/15 bg-white">
          <div className="text-[11px] uppercase tracking-[0.25em] font-semibold text-[#00B4A6] mb-3">
            Recommendation
          </div>
          <p className="font-display text-2xl md:text-3xl leading-snug tracking-tight text-[#0B2545] max-w-3xl">
            Use <span className="italic">02 · Lockup</span> as the master brand and{" "}
            <span className="italic">03 · Monogram</span> as the app / favicon mark. Together
            they form a complete identity system.
          </p>
        </div>

        <section data-testid="brand-kit-section" className="mt-24">
          <div className="mb-10 md:mb-14 max-w-3xl">
            <div className="text-[11px] uppercase tracking-[0.25em] font-semibold text-[#00B4A6] mb-3">
              Brand Kit · Based on Concept 01
            </div>
            <h2 className="font-display text-3xl md:text-5xl tracking-tight leading-[0.95] text-[#0B2545]">
              The full system.
              <br />
              <span className="italic">Intelligence, in operation.</span>
            </h2>
            <p className="mt-5 text-base md:text-lg text-[#4A4A4A] max-w-2xl">
              Concept 01 extended into a working identity — master lockup, colour applications,
              mono variants, and the OQ monogram for app icons and favicons.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            {KIT.map((l) => (
              <article
                key={l.id}
                data-testid={`kit-card-${l.id}`}
                className={`group border border-[#0B2545]/15 bg-white overflow-hidden ${l.span ? "md:col-span-2" : ""}`}
              >
                <div
                  className={`relative ${l.span ? "aspect-[16/8]" : "aspect-[4/3]"} flex items-center justify-center overflow-hidden`}
                  style={{ backgroundColor: l.bg }}
                >
                  <img
                    src={l.src}
                    alt={l.title}
                    data-testid={`kit-img-${l.id}`}
                    className="max-w-[85%] max-h-[85%] object-contain"
                  />
                </div>
                <div className="p-6 md:p-8 flex items-center justify-between gap-6">
                  <div>
                    <h3 className="font-display text-xl md:text-2xl leading-tight tracking-tight text-[#0B2545]">
                      {l.title}
                    </h3>
                    <p className="mt-2 text-sm text-[#4A4A4A] leading-relaxed max-w-xl">
                      {l.desc}
                    </p>
                  </div>
                  <a
                    href={l.src}
                    download={l.file}
                    data-testid={`kit-download-${l.id}`}
                    className="shrink-0 inline-flex items-center gap-2 px-4 py-2.5 border border-[#0B2545] text-[#0B2545] text-[11px] uppercase tracking-[0.2em] font-semibold hover:bg-[#0B2545] hover:text-white transition-colors"
                  >
                    <Download size={14} /> PNG
                  </a>
                </div>
              </article>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}
